import classNames from 'classnames';
import { FilterKAI, IconCancel, IconRegenerate, IconAccept } from 'components/Icons';
import { useAIQuery } from 'presentation/components/KAI/KAI.hook';
import KAILoader from 'presentation/components/KAI/KAILoader';
import { rewriteMessage } from 'presentation/components/KAI/SystemPrompts/Rewrite';
import { useEffect, useState } from 'react';
import { Whisper, Tooltip } from 'rsuite';
import DOMPurify from 'dompurify';
import { convertFromShortcodes } from '../../utils/emojiParser';
import './AutoEnhancer.scss';
import { AIQueryTypes } from '@hone-automation/common';

type AutoEnhancerProps = {
  message: string;
  toolbarOpen?: boolean;
  rewriteMessageCallBack: (text: string) => void;
  onCancel?: () => void;
  onKAIopen?: (open: boolean) => void;
};

const AutoEnhancer = (props: AutoEnhancerProps) => {
  const { message, toolbarOpen, rewriteMessageCallBack, onCancel, onKAIopen } = props;
  const [text, setText] = useState(message);
  const [KAIclicked, setKAIclicked] = useState(false);

  const [kayResponse, setKayResponse] = useState<string>('');

  const { sendQuery } = useAIQuery();

  const handleRewrite = () => {
    rewriteMessageCallBack(kayResponse);
    setKAIclicked(false);
  };

  useEffect(() => {
    if (KAIclicked) {
      onKAIopen?.(true);
    }
    return () => {
      onKAIopen?.(false);
    };
  }, [KAIclicked]);

  const handleRequestRewrite = async () => {
    const prompt = rewriteMessage(message);
    if (!prompt) return;
    const rewrite = await sendQuery(prompt, AIQueryTypes.Rewrite);

    const content = rewrite.content
      ? rewrite.content
          .replace(/^```json\n/, '')
          .replace(/\n```$/, '')
          .replace(/^\n/, '')
          .replace(/\n$/, '')
      : null;
    if (content) {
      try {
        const parsedData = JSON.parse(content);
        const improvedSentence = parsedData.improvedSentence;

        const purifyConfig = {
          ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'a', 'ul', 'ol', 'li', 'p', 'br', 'strike', 'del'],
          ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class'],
          ADD_ATTR: ['target', 'rel'],
          KEEP_CONTENT: true,
        };

        const sanitizedResponse = convertFromShortcodes(DOMPurify.sanitize(improvedSentence, purifyConfig));

        setKayResponse(sanitizedResponse);
      } catch (e) {
        console.error('Error parsing or sanitizing KAI response:', e, message, prompt, rewrite);
        setKayResponse('Error processing suggestion. Please retry');
      }
    } else {
      setKayResponse('No content available. Please retry');
    }
  };

  return (
    <div className={classNames('message-kai', { 'no-events': !KAIclicked })}>
      <div className="message-kai-result-container">
        <div
          className={classNames('message-kai-result-container-result', {
            'no-visible': !KAIclicked,
            disabled: message === '',
          })}
        >
          <div className={classNames('message-kai-result-container-result-header')}>MESSAGE SUGGESTION</div>
          <div className={classNames('message-kai-result-container-result-content')}>
            {kayResponse === '' ? (
              <KAILoader message="Rephrasing" small={true} />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: kayResponse }} />
            )}
          </div>
        </div>
      </div>
      <div className="message-kai-actions">
        {!KAIclicked ? (
          <div className={classNames('message-kai-actions before', { 'no-header': !toolbarOpen })}>
            <Whisper
              placement="left"
              trigger="hover"
              delayOpen={800}
              delayClose={10}
              speaker={<Tooltip>Enhance the message</Tooltip>}
            >
              <div
                className={classNames('message-kai-actions-icon', {
                  disabled: message === '',
                  clicked: KAIclicked,
                  'enhance-button': true,
                })}
                onClick={() => {
                  setKAIclicked(true);
                  setKayResponse('');
                  setTimeout(handleRequestRewrite, 20);
                }}
              >
                <FilterKAI />
              </div>
            </Whisper>
          </div>
        ) : (
          <div className="message-kai-actions after">
            <Whisper
              placement="left"
              trigger="hover"
              delayOpen={800}
              delayClose={10}
              speaker={<Tooltip>Cancel</Tooltip>}
            >
              <div
                className="message-kai-actions-icon"
                onClick={() => {
                  setKAIclicked(false);
                }}
              >
                <IconCancel />
              </div>
            </Whisper>
            <Whisper placement="left" trigger="hover" delayOpen={800} speaker={<Tooltip>Regenerate</Tooltip>}>
              <div
                className="message-kai-actions-icon"
                onClick={() => {
                  setKayResponse('');
                  setTimeout(handleRequestRewrite, 20);
                }}
              >
                <IconRegenerate />
              </div>
            </Whisper>
            <Whisper
              placement="left"
              trigger="hover"
              delayOpen={800}
              delayClose={10}
              speaker={<Tooltip>Use the Enhanced Message</Tooltip>}
            >
              <div className="message-kai-actions-icon" onClick={handleRewrite}>
                <IconAccept />
              </div>
            </Whisper>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoEnhancer;
